import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { authClient, useSession } from "@/lib/auth-client";

/**
 * Admin "view as" over Better Auth's native impersonation. The server swaps
 * the session cookie, so every fetch in the app becomes the target's — which
 * is exactly the "same view and edit rights" the feature promises. Both
 * transitions hard-reload rather than navigate: TanStack Query's cache and
 * every mounted component hold the PREVIOUS identity's data, and a reload is
 * the only cheap way to guarantee none of it leaks across.
 */
function readImpersonatedBy(session: unknown): string | null {
  if (typeof session !== "object" || session === null) return null;
  const value = (session as Record<string, unknown>).impersonatedBy;
  return typeof value === "string" && value.length > 0 ? value : null;
}

function reloadToDashboard(): void {
  window.location.assign("/dashboard");
}

function messageOf(error: unknown, fallback: string): string {
  if (typeof error === "object" && error !== null) {
    const msg = (error as Record<string, unknown>).message;
    if (typeof msg === "string" && msg) return msg;
  }
  return fallback;
}

export function useImpersonation() {
  const { data: session } = useSession();
  const impersonatedBy = readImpersonatedBy(session?.session);

  const start = useMutation({
    mutationFn: async (userId: string) => {
      const res = await authClient.admin.impersonateUser({ userId });
      if (res.error) {
        throw new Error(messageOf(res.error, "Couldn't start viewing as this person"));
      }
    },
    onSuccess: reloadToDashboard,
    onError: (e) => toast.error(messageOf(e, "Something went wrong")),
  });

  /**
   * Resolve the account to view a PERSON as, creating one if they have never
   * signed in (WI-150) — the server makes a credential-less row on demand and
   * links it. Two steps rather than one because the id only exists after the
   * first: the button has a person, not a user.
   *
   * Deliberately does NOT start. Viewing as someone who holds admin access has
   * to be confirmed first (HR feedback 2026-09-22), and the role is only known
   * once this call returns — so resolving and starting are separate, and the
   * caller decides whether a confirm belongs in between. It resolves to `null`
   * rather than throwing, because every refusal here has already been shown to
   * the admin as a toast.
   */
  const prepareForPerson = useMutation({
    mutationFn: async (personId: string) => {
      const res = await fetch(`/api/persons/${personId}/view-as-account`, { method: "POST" });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        const message =
          body !== null && typeof body === "object" && "error" in body
            ? String(body.error)
            : "Couldn't prepare an account to view as";
        throw new Error(message);
      }
      const userId = body?.data?.userId;
      if (typeof userId !== "string" || !userId) {
        throw new Error("Couldn't prepare an account to view as");
      }
      const role = body?.data?.role;
      return { userId, role: typeof role === "string" ? role : null };
    },
    // No `onSuccess` reload — preparing is not yet a transition.
    onError: (e) => toast.error(messageOf(e, "Something went wrong")),
  });

  const stop = useMutation({
    mutationFn: async () => {
      const res = await authClient.admin.stopImpersonating();
      if (res.error) {
        throw new Error(messageOf(res.error, "Couldn't stop viewing as this person"));
      }
    },
    onSuccess: reloadToDashboard,
    onError: (e) => toast.error(messageOf(e, "Something went wrong")),
  });

  return {
    /** True when the current session is an admin viewing as someone else. */
    active: impersonatedBy !== null,
    /** The real admin's user id while active; null otherwise. */
    impersonatedBy,
    /** Start viewing as `userId`; on success hard-reloads to /dashboard. */
    start: (userId: string) =>
      start.mutateAsync(userId).then(
        () => undefined,
        () => undefined,
      ),
    /**
     * Resolve (creating if needed) the account to view a PERSON as. Returns
     * `{ userId, role }`, or null when it could not be prepared — the reason
     * has already been shown as a toast. Call `start(userId)` afterwards.
     */
    prepareForPerson: (personId: string) =>
      prepareForPerson.mutateAsync(personId).then(
        (r) => r,
        () => null,
      ),
    /** Return to the admin's own session; on success hard-reloads to /dashboard. */
    stop: () =>
      stop.mutateAsync().then(
        () => undefined,
        () => undefined,
      ),
    isPending: start.isPending || prepareForPerson.isPending || stop.isPending,
  };
}
